import { Theme, css } from '@emotion/react';
import { FactoryOpts } from 'imask';
import { FC, ReactNode } from 'react';
import { Controller, RegisterOptions, useFormContext } from 'react-hook-form';
import { IMaskInput } from 'react-imask';

import { fontStyles } from '~src/theme/fontStyles';
import { mediaQueries } from '~src/theme/mediaQueries';
import { getBorderColor, getLabelColor } from '~src/ui/common-styles';
import { InputError } from '~src/ui/input-error';

import { AddressFormFields } from '~address-form/types';

import { getValidationValue } from '../utils/hook-form-utils';

interface MaskedInputProps extends React.HTMLAttributes<HTMLInputElement> {
  name: keyof AddressFormFields;
  maskOptions: FactoryOpts;
  onFocus: () => void;
  onBlur: () => void;
  isFocused: boolean;
  label?: string;
  warning?: boolean;
  extensionElement?: ReactNode;
  type?: string;
  validationRules?: RegisterOptions;
  dataTest?: string;
}

export const MaskedInput: FC<MaskedInputProps> = ({
  maskOptions,
  name,
  onFocus,
  onBlur,
  isFocused,
  label,
  extensionElement,
  validationRules,
  warning,
  dataTest,
  ...props
}) => {
  const {
    control,
    formState: { errors },
    getValues,
  } = useFormContext<AddressFormFields>();

  const value = getValues(name);
  const isLabelRaised = isFocused || !!value || !!extensionElement;
  const hasError = !!errors[name];

  return (
    <>
      <div css={styles.wrapper} onFocus={onFocus}>
        <label
          htmlFor={name}
          css={styles.label(isLabelRaised, hasError, warning)}
          aria-hidden={hasError ? 'false' : 'true'}
        >
          {label}
        </label>
        <div css={styles.inputWrapper(isFocused, hasError, warning)}>
          {extensionElement}
          <Controller
            rules={validationRules}
            name={name}
            control={control}
            render={({ field: { onChange, onBlur: onFormBlur, value, ref } }) => (
              <IMaskInput
                {...props}
                {...maskOptions}
                css={styles.input(Boolean(extensionElement))}
                inputRef={ref}
                value={value as string}
                onBlur={() => {
                  onBlur();
                  onFormBlur();
                }}
                onAccept={onChange}
                data-test={dataTest}
                aria-invalid={hasError}
                aria-required={!!getValidationValue(validationRules?.required)}
              />
            )}
          />
        </div>
      </div>
      {hasError && <InputError dataTest={`${name}-error`}>{errors[name]?.message}</InputError>}
    </>
  );
};

export const styles = {
  wrapper: css({
    position: 'relative',
    height: '60px',
    width: '100%',
    ...fontStyles.px14_20,
  }),
  inputWrapper: (focused: boolean, error: boolean, warning?: boolean) => (theme: Theme) =>
    css({
      backgroundColor: 'transparent',
      borderColor: getBorderColor(focused, error, warning)(theme),
      borderStyle: 'solid',
      borderWidth: '1px',
      borderRadius: '2px',
      transition: 'all 0.2s',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    }),
  input: (showExtensionElement: boolean) => (theme: Theme) =>
    css({
      border: 'none',
      color: theme.black,
      height: '100%',
      width: '100%',
      outline: 'none',
      WebkitAppearance: 'none',
      boxSizing: 'border-box',
      padding: showExtensionElement ? '30px 16px 12px 4px' : '30px 16px 12px 16px',
      ...fontStyles.px14_20,
      resize: 'none',
      overflow: 'hidden',
      [mediaQueries.S]: {
        ...fontStyles.px16_20, // disable auto-zoom on ios.
      },
    }),
  label: (focused: boolean, error?: boolean, warning?: boolean) => (theme: Theme) =>
    css({
      position: 'absolute',
      display: 'flex',
      justifyContent: 'space-between',
      top: focused ? '11px' : '50%',
      transform: `translate(0, ${focused ? '0' : '-50%'})`,
      left: '16px',
      right: '16px',
      overflow: 'hidden',
      color: getLabelColor(!!error, warning)(theme),
      transition: 'all .1s ease-in-out',
      pointerEvents: 'none',
      cursor: 'text',
      whiteSpace: 'nowrap',
      ...(focused ? fontStyles.px12_16 : fontStyles.px16_20),
    }),
  extensionStyles: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingLeft: '8px',
    paddingRight: '17px',
  },
};
